import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { fadeIn, fadeInUp, popIn, staggerContainer } from '@/shared/motion/motionPresets';
import type { ChatMessage } from '../types/navigator-chat.types';

type ChatThreadProps = {
  messages: ChatMessage[];
  isTyping: boolean;
  onQuickReply: (reply: string) => void;
  /** Click handler for the Generate Care Guide CTA (generation already runs in the background). */
  onGenerateCareGuide?: () => void;
};

const AssistantAvatar = () => (
  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sage text-white">
    <Sparkles className="h-4 w-4" aria-hidden />
  </span>
);

export const ChatThread = forwardRef<HTMLDivElement, ChatThreadProps>(
  ({ messages, isTyping, onQuickReply, onGenerateCareGuide }, scrollRef) => {
    return (
      <div className="max-h-[560px] min-h-[420px] space-y-5 overflow-y-auto p-6">
        {messages.map((message) => (
          <div key={message.id}>
            <motion.div
              variants={message.kind === 'generate-care-guide' ? popIn : fadeInUp}
              initial="initial"
              animate="animate"
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'items-start'}`}
            >
              {message.role === 'assistant' && <AssistantAvatar />}
              <div
                className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  message.role === 'user'
                    ? 'bg-sage text-white'
                    : 'bg-card-subtle text-text-primary'
                }`}
              >
                <p>{message.content}</p>
                {message.kind === 'generate-care-guide' && (
                  <motion.button
                    type="button"
                    onClick={onGenerateCareGuide}
                    variants={popIn}
                    initial="initial"
                    animate="animate"
                    transition={{ delay: 0.25 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-medium text-white shadow-[0_4px_14px_rgba(13,93,78,0.24)] transition-colors duration-150 ease-out hover:bg-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden />
                    Generate Care Guide
                  </motion.button>
                )}
              </div>
            </motion.div>

            {message.role === 'assistant' &&
              message.quickReplies &&
              message.quickReplies.length > 0 && (
                <motion.div
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                  className="ml-11 mt-3 flex flex-wrap gap-2"
                >
                  {message.quickReplies.map((reply) => (
                    <motion.button
                      key={reply}
                      type="button"
                      variants={fadeIn}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => onQuickReply(reply)}
                      className="rounded-full border border-border-subtle bg-card-base px-4 py-1.5 text-sm font-medium text-text-primary transition-colors duration-150 ease-out hover:border-sage hover:bg-sage-bg hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                    >
                      {reply}
                    </motion.button>
                  ))}
                </motion.div>
              )}
          </div>
        ))}

        {isTyping && (
          <motion.div
            variants={fadeIn}
            initial="initial"
            animate="animate"
            className="flex items-start gap-3"
          >
            <AssistantAvatar />
            <div className="flex items-center gap-1 rounded-2xl bg-card-subtle px-4 py-3">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary [animation-delay:-0.32s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary [animation-delay:-0.16s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary" />
            </div>
          </motion.div>
        )}

        <div ref={scrollRef} />
      </div>
    );
  },
);

ChatThread.displayName = 'ChatThread';
